import { Fragment, useState, useEffect, useMemo, useRef } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { useCategoriesStore } from '@/store/categoriesStore'
import { useAuthStore } from '@/store/authStore'

// Define types for temporary use until proper imports can be resolved
interface Category {
  id: string
  name: string
  color: string
  type?: string
  subcategories?: SubCategory[]
}

interface SubCategory {
  id: string
  name: string
}

type CategoryWithSubcategories = Category & {
  subcategories?: SubCategory[]
}

type CategoryWithParent = SubCategory & {
  parentCategory: string
  parentCategoryId: string
  color: string
}

interface CategorySelectProps {
  value: {
    categoryId: string;
    category: string;
  };
  onChange: (categoryId: string, categoryName: string, isSubcategory?: boolean) => void;
  typeFilter?: string | null;
}

export function CategorySelect({ value, onChange, typeFilter = null }: CategorySelectProps) {
  const [open, setOpen] = useState(false)
  const [categoryQuery, setCategoryQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { categories, fetchCategories, loading } = useCategoriesStore()
  const { user } = useAuthStore()
  
  // Loading state alias for better readability
  const isLoading = loading
  
  // Fetch categories when component mounts
  useEffect(() => {
    if (user?.id) {
      fetchCategories(user.id)
    }
  }, [user?.id, fetchCategories])
  
  // Filter categories based on search query and type
  const filteredCategories = useMemo(() => {
    let filtered = categories;
    
    // Filter by type if typeFilter is provided
    if (typeFilter) {
      filtered = filtered.filter(category => category.type === typeFilter);
    }
    
    // Then filter by search query
    if (categoryQuery) {
      filtered = filtered.filter((category: Category) => {
        const matchesCategory = category.name.toLowerCase().includes(categoryQuery.toLowerCase())
        
        // Also check if any subcategories match
        const hasMatchingSubcategory = category.subcategories?.some(
          (sub: SubCategory) => sub.name.toLowerCase().includes(categoryQuery.toLowerCase())
        )
        
        return matchesCategory || hasMatchingSubcategory
      });
    }
    
    return filtered;
  }, [categories, categoryQuery, typeFilter])

  // Get all subcategories as flattened list for easier filtering
  const getAllSubcategories = useMemo(() => {
    const subs: CategoryWithParent[] = [];
    
    categories.forEach(category => {
      if (category.subcategories && category.subcategories.length) {
        category.subcategories.forEach((sub: SubCategory) => {
          subs.push({
            ...sub,
            parentCategory: category.name,
            parentCategoryId: category.id,
            color: category.color
          });
        });
      }
    });
    
    return subs;
  }, [categories]);
  
  // Filter subcategories by search query
  const filteredSubcategories = useMemo(() => {
    if (!categoryQuery) return [];
    
    return getAllSubcategories.filter((sub: CategoryWithParent) => 
      sub.name.toLowerCase().includes(categoryQuery.toLowerCase()) ||
      sub.parentCategory.toLowerCase().includes(categoryQuery.toLowerCase())
    );
  }, [getAllSubcategories, categoryQuery]);

  // Group categories by type for better organization
  const groupedCategories = useMemo(() => {
    const grouped: Record<string, Category[]> = {
      expense: [],
      income: [],
      other: []
    };
    
    filteredCategories.forEach(category => {
      if (category.type === 'expense') {
        grouped.expense.push(category);
      } else if (category.type === 'income') {
        grouped.income.push(category);
      } else {
        grouped.other.push(category);
      }
    });
    
    return grouped;
  }, [filteredCategories]);

  // Handle category or subcategory selection
  const handleCategorySelect = (item: CategoryWithSubcategories | CategoryWithParent) => {
    if ('parentCategory' in item) {
      // This is a subcategory
      onChange(item.id, `${item.parentCategory} → ${item.name}`, true)
    } else {
      // This is a main category
      onChange(item.id, item.name, false)
    }
    
    setOpen(false)
    setCategoryQuery('')
  }

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Find the category or subcategory details for display
  const getDisplayText = (): string => {
    if (!value.categoryId || !value.category) return '';
    
    // Use the provided category name directly if it has the subcategory delimiter
    if (value.category.includes('→')) {
      return value.category;
    }
    
    // Look through main categories
    const mainCategory = categories.find(cat => cat.id === value.categoryId);
    if (mainCategory) {
      return mainCategory.name;
    }
    
    // Look through subcategories
    for (const category of categories) {
      if (category.subcategories) {
        const subcategory = category.subcategories.find((sub: SubCategory) => sub.id === value.categoryId);
        if (subcategory) {
          return `${category.name} → ${subcategory.name}`;
        }
      }
    }
    
    // Fallback to the provided category name
    return value.category;
  }

  // Get color for the currently selected category or subcategory
  const getSelectedColor = (): string => {
    // Look for main category color
    const mainCategory = categories.find(cat => cat.id === value.categoryId);
    if (mainCategory) {
      return mainCategory.color;
    }
    
    // Look for subcategory color (use parent's color)
    for (const category of categories) {
      if (category.subcategories) {
        const subcategory = category.subcategories.find((sub: SubCategory) => sub.id === value.categoryId);
        if (subcategory) {
          return category.color;
        }
      }
    }
    
    // Default color
    return '#439476';
  }

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Input field / trigger */}
      <div 
        className={`flex items-center relative h-10 w-full transition-all duration-200 rounded-xl overflow-hidden
          ${open 
            ? 'ring-2 ring-[#439476] border-transparent' 
            : 'border border-[#2F2F2F]/10 dark:border-[#333840] hover:border-[#439476]/50'
          }
          bg-white dark:bg-[#1E293B] cursor-pointer`
        }
        onClick={() => {
          if (!isLoading) {
            setOpen(!open);
            setCategoryQuery('');
          }
        }}
      >
        {isLoading ? (
          <div className="flex items-center px-4 space-x-2 text-[#6B7280] dark:text-[#A3A3A3]">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#439476] border-t-transparent"></div>
            <span>Loading...</span>
          </div>
        ) : (
          <>
            <div className="pl-4 pr-10 w-full flex items-center min-h-[40px]">
              {value.categoryId && (
                <div 
                  className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                  style={{ backgroundColor: getSelectedColor() }}
                />
              )}
              <div className="flex-1 text-[#1C1C1C] dark:text-white truncate">
                {getDisplayText() || (
                  <span className="text-[#6B7280] dark:text-[#A3A3A3]">Select category...</span>
                )}
              </div>
            </div>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#439476]">
              <ChevronDown 
                className="h-4 w-4 transition-transform duration-200 ease-in-out" 
                style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)' }} 
              />
            </div>
          </>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div 
          className="absolute z-50 mt-1 w-full rounded-xl py-1 shadow-lg bg-white dark:bg-[#1E293B] border border-[#2F2F2F]/10 dark:border-[#333840] focus:outline-none text-sm max-h-60 overflow-auto"
        >
          {/* Search bar at the top of dropdown */}
          <div className="sticky top-0 bg-white dark:bg-[#1E293B] border-b border-[#2F2F2F]/10 dark:border-[#333840] p-2 z-10">
            <div className="relative">
              <Search className="w-4 h-4 text-[#6B7280] dark:text-[#A3A3A3] absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                className="w-full py-1.5 pl-9 pr-3 rounded-lg text-sm bg-gray-50 dark:bg-[#141E30] border border-[#2F2F2F]/10 dark:border-[#333840] focus:outline-none focus:ring-1 focus:ring-[#439476]"
                placeholder="Search categories..."
                value={categoryQuery}
                onChange={(e) => setCategoryQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {(filteredCategories.length === 0 && filteredSubcategories.length === 0) && categoryQuery !== '' ? (
            <div className="relative cursor-default select-none py-4 px-4 text-center text-[#6B7280] dark:text-[#A3A3A3]">
              <div className="text-lg mb-1">🔍</div>
              No categories found matching "{categoryQuery}"
            </div>
          ) : isLoading ? (
            <div className="py-8 text-center">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#439476] border-t-transparent"></div>
              <p className="mt-2 text-[#6B7280] dark:text-[#A3A3A3]">Loading categories...</p>
            </div>
          ) : (
            <>
              {/* Show matching subcategories at the top when searching */}
              {categoryQuery && filteredSubcategories.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-1.5 text-xs font-medium text-[#6B7280] dark:text-[#A3A3A3] bg-gray-50 dark:bg-[#141E30]">
                    Matching Subcategories
                  </div>
                  <div className="py-1">
                    {filteredSubcategories.map((sub) => (
                      <div
                        key={`${sub.parentCategoryId}-${sub.id}`}
                        className="relative cursor-pointer select-none py-2 pl-8 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategorySelect(sub);
                        }}
                      >
                        <div className="flex items-center">
                          <div
                            className="w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: sub.color }}
                          />
                          <span className="block truncate">
                            {sub.name} <span className="text-xs opacity-70 ml-1">({sub.parentCategory})</span>
                          </span>
                        </div>
                        {value.categoryId === sub.id && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                            <Check className="h-4 w-4" aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expense Categories */}
              {groupedCategories.expense.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-1.5 text-xs font-medium text-[#6B7280] dark:text-[#A3A3A3] bg-gray-50 dark:bg-[#141E30]">
                    Expense Categories
                  </div>
                  <div className="py-1">
                    {groupedCategories.expense.map((category) => (
                      <Fragment key={category.id}>
                        {/* Parent Category */}
                        <div
                          className="relative cursor-pointer select-none py-2 pl-4 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategorySelect(category);
                          }}
                        >
                          <div className="flex items-center">
                            <div
                              className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="block truncate">
                              {category.name}
                            </span>
                          </div>
                          {value.categoryId === category.id && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </div>

                        {/* Subcategories - only show if category matches or no search */}
                        {(!categoryQuery || category.name.toLowerCase().includes(categoryQuery.toLowerCase())) && category.subcategories && category.subcategories.length > 0 && (
                          <div className="border-l border-[#439476]/20 ml-4 pl-1">
                            {category.subcategories.map((subcategory: SubCategory) => (
                              <div
                                key={subcategory.id}
                                className="relative cursor-pointer select-none py-1.5 pl-7 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategorySelect({
                                    ...subcategory,
                                    parentCategory: category.name,
                                    parentCategoryId: category.id,
                                    color: category.color
                                  });
                                }}
                              >
                                <div className="flex items-center">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0"
                                    style={{ backgroundColor: category.color }}
                                  />
                                  <span className="block truncate text-sm">
                                    {subcategory.name}
                                  </span>
                                </div>
                                {value.categoryId === subcategory.id && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* Income Categories */}
              {groupedCategories.income.length > 0 && (
                <div className="mb-2">
                  <div className="px-3 py-1.5 text-xs font-medium text-[#6B7280] dark:text-[#A3A3A3] bg-gray-50 dark:bg-[#141E30]">
                    Income Categories
                  </div>
                  <div className="py-1">
                    {groupedCategories.income.map((category) => (
                      <Fragment key={category.id}>
                        {/* Parent Category */}
                        <div
                          className="relative cursor-pointer select-none py-2 pl-4 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategorySelect(category);
                          }}
                        >
                          <div className="flex items-center">
                            <div
                              className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="block truncate">
                              {category.name}
                            </span>
                          </div>
                          {value.categoryId === category.id && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </div>

                        {/* Subcategories - only show if category matches or no search */}
                        {(!categoryQuery || category.name.toLowerCase().includes(categoryQuery.toLowerCase())) && category.subcategories && category.subcategories.length > 0 && (
                          <div className="border-l border-[#439476]/20 ml-4 pl-1">
                            {category.subcategories.map((subcategory: SubCategory) => (
                              <div
                                key={subcategory.id}
                                className="relative cursor-pointer select-none py-1.5 pl-7 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategorySelect({
                                    ...subcategory,
                                    parentCategory: category.name,
                                    parentCategoryId: category.id,
                                    color: category.color
                                  });
                                }}
                              >
                                <div className="flex items-center">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0"
                                    style={{ backgroundColor: category.color }}
                                  />
                                  <span className="block truncate text-sm">
                                    {subcategory.name}
                                  </span>
                                </div>
                                {value.categoryId === subcategory.id && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Categories (if any) */}
              {groupedCategories.other.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-[#6B7280] dark:text-[#A3A3A3] bg-gray-50 dark:bg-[#141E30]">
                    Other Categories
                  </div>
                  <div className="py-1">
                    {groupedCategories.other.map((category) => (
                      <Fragment key={category.id}>
                        {/* Parent Category */}
                        <div
                          className="relative cursor-pointer select-none py-2 pl-4 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategorySelect(category);
                          }}
                        >
                          <div className="flex items-center">
                            <div
                              className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="block truncate">
                              {category.name}
                            </span>
                          </div>
                          {value.categoryId === category.id && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </div>

                        {/* Subcategories - only show if category matches or no search */}
                        {(!categoryQuery || category.name.toLowerCase().includes(categoryQuery.toLowerCase())) && category.subcategories && category.subcategories.length > 0 && (
                          <div className="border-l border-[#439476]/20 ml-4 pl-1">
                            {category.subcategories.map((subcategory: SubCategory) => (
                              <div
                                key={subcategory.id}
                                className="relative cursor-pointer select-none py-1.5 pl-7 pr-9 transition-colors hover:bg-[#439476]/10 hover:text-[#439476] text-[#1C1C1C] dark:text-white"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategorySelect({
                                    ...subcategory,
                                    parentCategory: category.name,
                                    parentCategoryId: category.id,
                                    color: category.color
                                  });
                                }}
                              >
                                <div className="flex items-center">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0"
                                    style={{ backgroundColor: category.color }}
                                  />
                                  <span className="block truncate text-sm">
                                    {subcategory.name}
                                  </span>
                                </div>
                                {value.categoryId === subcategory.id && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                                    <Check className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
} 